import * as React from 'react';
import axios, {AxiosResponse} from 'axios';

import {QuestionsEntry, ScanEntry} from '../data/Data';
import {Router} from '../Router';
import {Serializer} from '../Serializer';
import {URLData} from '../data/URLData';
import {WordPressContext, WordPressData, WordPressQuestionData} from '../data/WordPressData';

type PostData = {
    shortcode?: string;
    state: string;
    post_id: number;
};

type Response = {
    shortcode: string;
};

type State = {
    code: string | undefined;
    finished: boolean;
    postId: number;
    shouldShowButton: boolean;
    scans: ScanEntry[];
    tooltip: number | undefined;
    urlData: URLData;
};

export type ManageableState = {
    state: State;
    dispatch: React.Dispatch<StateAction>;
};

export const StateContext: React.Context<ManageableState> = React.createContext<ManageableState>({
    state: {
        code: undefined,
        finished: false,
        postId: -1,
        shouldShowButton: false,
        scans: [],
        tooltip: undefined,
        urlData: {},
    },
    dispatch: (action: StateAction): void => undefined,
});

type StateProps = {
    children: React.ReactNode;
    data: WordPressData[];
    context: WordPressContext;
};

export type StateAction = AnswerAction | TooltipAction | CodeAction;

export type AnswerAction = {
    type: 'ANSWER';
    payload: {
        scanId: number;
        questionId: number;
        answerId: number;
    };
};

export type TooltipAction = {
    type: 'TOOLTIP';
    payload: {
        questionId: number | undefined;
    };
};

export type CodeAction = {
    type: 'CODE';
    payload: {
        code: string;
    };
};

function allQuestionsComplete(questions: QuestionsEntry): boolean {
    return Object.keys(questions).every((questionId: string): boolean => {
        return questions[questionId].answer !== undefined;
    });
}

function reducer(state: State, action: StateAction): State {
    switch (action.type) {
        case 'CODE':
            return {
                ...state,
                code: action.payload.code,
            };
        case 'TOOLTIP':
            return {
                ...state,
                tooltip:
                    state.tooltip === action.payload.questionId
                        ? undefined
                        : action.payload.questionId,
            };
        case 'ANSWER':
            const scans: ScanEntry[] = state.scans.map(
                (scan: ScanEntry): ScanEntry => {
                    if (scan.id !== action.payload.scanId) {
                        return scan;
                    }

                    const questions: QuestionsEntry = {
                        ...scan.questions,
                        [action.payload.questionId]: {
                            ...scan.questions[action.payload.questionId],
                            answer: action.payload.answerId,
                        },
                    };

                    const complete: boolean = allQuestionsComplete(questions);

                    return {
                        ...scan,
                        questions,
                        complete,
                    };
                },
            );

            return {
                ...state,
                scans,
                finished: scans.every(
                    (scan: ScanEntry): boolean => scan.complete,
                ),
                urlData: {
                    ...state.urlData,
                    [action.payload.scanId]: {
                        ...state.urlData[action.payload.scanId],
                        [action.payload.questionId]: action.payload.answerId,
                    },
                },
            };
        default:
            return state;
    }
}

function initialState(
    wordPressData: WordPressData[],
    urlData: URLData,
    selected: number,
): State {
    const scans: ScanEntry[] = wordPressData.map(
        (data: WordPressData, index: number): ScanEntry => {
            const questions: QuestionsEntry = data.posts.reduce(
                (
                    acc: QuestionsEntry,
                    post: WordPressQuestionData,
                ): QuestionsEntry => {
                    const answer: number | undefined =
                        urlData[String(data.scan)]?.[String(post.id)];

                    return {
                        ...acc,
                        [String(post.id)]: {
                            ...post,
                            title: post.question,
                            answer,
                        },
                    };
                },
                {},
            );

            return {
                id: data.scan,
                index,
                selected:
                    (isNaN(selected) && index === 0) || selected === data.scan,
                first: data.first,
                title: data.title,
                complete: allQuestionsComplete(questions),
                questions,
            };
        },
    );

    return {
        code: wordPressData[0].code,
        postId: wordPressData[0].post_id,
        shouldShowButton: wordPressData[0].shouldShowButton,
        tooltip: undefined,
        finished: scans.every((scan: ScanEntry): boolean => scan.complete),
        scans,
        urlData,
    };
}

function save(
    code: string | undefined,
    postId: number,
    state: string,
    ajaxUrl: string,
): Promise<AxiosResponse<Response>> {
    const action: string =
        code !== undefined
            ? 'zmp_save_quickscan'
            : 'zmp_quickscan_create_shortcode';
    const url: string = `${ajaxUrl}?action=${action}`;
    const data: PostData = {
        post_id: postId,
        state,
    };

    if (code) {
        data.shortcode = code;
    }

    return axios.post(url, data);
}

// tslint:disable-next-line function-name
export function StateProvider(props: StateProps): JSX.Element {
    const [state, dispatch] = React.useReducer(
        reducer,
        initialState(
            props.data,
            Serializer.deserialize(Router.get()),
            parseInt(Router.hash(), 10),
        ),
    );

    React.useEffect((): void => {
        if (Object.keys(state.urlData).length === 0) {
            return;
        }

        if (state.code === undefined) {
            Router.set(`${Serializer.serialize(state.urlData)}`);
        } else {
            Router.set(
                `${Serializer.serialize(state.urlData)}&code=${state.code}`,
            );
        }

        save(
            state.code,
            state.postId,
            Serializer.serialize(state.urlData),
            props.context.ajax_url,
        ).then((res: AxiosResponse<Response>): void => {
            dispatch({
                type: 'CODE',
                payload: {
                    code: res.data.shortcode,
                },
            });
        });
    }, [state.urlData, state.code]);

    return (
        <StateContext.Provider value={{state, dispatch}}>
            {props.children}
        </StateContext.Provider>
    );
}
